import { useRef, useState, MouseEvent } from 'react';

import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import ButtonGroup from '@mui/material/ButtonGroup';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Popper, { type PopperPlacementType } from '@mui/material/Popper';
import type { Theme } from '@mui/material/styles';

const buttonBorderRadiusSx = {
  borderRadius: (theme: Theme) => theme.shape.borderRadius,
};
const sxPopperContainer = { zIndex: 3 };
const colorBySeverity = {
  warning: 'warning',
  error: 'error',
} as const;
const popperContentStyle = (placement: PopperPlacementType) => ({
  transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
});

export type MenuOptionType = {
  label: string;
  onSelect: (e: MouseEvent<HTMLLIElement>) => void;
};

interface DropdownSplitButtonProps {
  options: Array<MenuOptionType>;
  button?: () => React.ReactNode;
  severity?: 'warning' | 'error';
  disabled?: boolean;
}

export default function DropdownSplitButton({
  button,
  severity,
  disabled = false,
  options,
}: DropdownSplitButtonProps) {
  const [open, setOpen] = useState(false);

  const anchorRef = useRef<HTMLDivElement>(null);
  const color = severity ? colorBySeverity[severity] : undefined;

  const handleToggle = () => setOpen((prevOpen) => !prevOpen);
  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  return (
    <>
      <ButtonGroup ref={anchorRef}>
        <Button
          size="small"
          onClick={handleToggle}
          sx={buttonBorderRadiusSx}
          color={color}
          disabled={disabled}
        >
          <ArrowDropDownIcon />
        </Button>

        {button?.()}
      </ButtonGroup>

      <Popper
        transition
        open={open}
        disablePortal
        sx={sxPopperContainer}
        anchorEl={anchorRef.current}
      >
        {({ TransitionProps, placement }) => (
          <Grow {...TransitionProps} style={popperContentStyle(placement)}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem>
                  {options.map(({ label, onSelect }) => (
                    <MenuItem
                      key={label}
                      onClick={(e) => {
                        onSelect(e);
                        setOpen(false);
                      }}
                    >
                      {label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
